import { Directive, EventEmitter, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DateFilterTypeEnum } from "src/app/enums/dateFilterType.enum";
import { SelectedDateRange } from "src/app/models/dateRange.model";
import { Lookup } from "src/app/models/lookup.model";
import {
  MappingHistory,
  MappingHistoryPayload,
} from "src/app/models/mappingHistory.model";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { ParameterLookup } from "src/app/models/parameter.model";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { MappingGroupService } from "src/app/services/mapping-group.service";
import { MappingHistoryService } from "src/app/services/mapping-history.service";

/**
 * Renderer-agnostic state shared by the real-time map surfaces: mapping-group selection,
 * dust/zoned-risk history and its play/pause timeline. Holds no renderer API references so
 * both the Google (zones) and MapLibre (dashboard) components can extend it.
 *
 * Subclasses MUST call `super.ngOnDestroy()` from their own `ngOnDestroy`, otherwise the
 * subscriptions started here outlive the component.
 *
 * Decorated but selector-less so Angular compiles the DI and lifecycle hooks it declares;
 * it is never declared in a module and exists only to be extended.
 */
@Directive()
export class BaseRealtimeMap implements OnDestroy {
  /* header values */
  selectedOrg: OrganizationLevel;
  selectedParameter: ParameterLookup;
  selectedDateRange: SelectedDateRange;
  /* extended map options */
  showNodes = true;
  selectedZoneId: string;
  zonesList: Lookup[] = [];
  dustLayer: MappingHistory[] = [];
  /* play/pause button related properties*/
  showPlayOption: boolean;
  currentKmlIndex: number | null;
  activePlayOption: string;
  isPlaying = false;
  pausedIndex: number = null;
  markersTimeout: ReturnType<typeof setTimeout>[] = [];
  markerEvent = new EventEmitter<boolean>();
  showWindDir = true;
  windDirEvent = new EventEmitter<boolean>();
  mappingGroupContainer: HTMLDivElement;

  /** Named apart from any `destroy$` a subclass declares for its own streams. */
  protected readonly baseDestroy$ = new Subject<void>();

  constructor(
    public mappingGroupService: MappingGroupService,
    public mappingHistoryService: MappingHistoryService,
    public dateFormatService: DateFormatService
  ) {
    this.mappingGroupService
      .getAll()
      .pipe(takeUntil(this.baseDestroy$))
      .subscribe((res) => {
        if (res && res.length > 0) {
          this.zonesList = res;
          this.selectedZoneId = String(this.zonesList[0].id);
        }
      });
  }

  ngOnDestroy(): void {
    this.pausePlay();
    this.baseDestroy$.next();
    this.baseDestroy$.complete();
  }

  // Shared by both map surfaces so their wind cones stay the same length: 200 m in calm air, growing on a log
  // scale to 2000 m at about 14 m/s.
  protected calculateConeLength(windSpeed: number): number {
    const minLength = 200;
    const maxLength = 2000;
    if (!windSpeed || windSpeed <= 0) return minLength;
    const length = minLength + (Math.log(windSpeed + 1) / Math.log(15)) * (maxLength - minLength);
    return Math.min(length, maxLength);
  }

  /**
   * Detach the previously rendered dust/zoned-risk overlay. Renderer-specific, so the
   * base class does nothing and the Google surface overrides it.
   */
  protected clearPreviousKmlLayer(): void {
    /* no overlay to detach unless a renderer tracks one */
  }

  getUpdatedZoneData() {
    if (this.activePlayOption) {
      this.dustLayer = [];
      this.showPlayOption = false;
      this.clearPreviousKmlLayer();
      this.pausePlay();
      this.markerEvent.next(true);
      this.getZoneRiskData();
    }
  }

  getMappingGroupSelectBox() {
    const selectContainer = document.createElement("div");
    selectContainer.classList.add("mapping-group-select-container");

    const select = document.createElement("select");
    select.name = "my-select";

    this.zonesList.forEach((option) => {
      const selectOption = document.createElement("option");
      selectOption.value = option.id.toString();
      selectOption.textContent = option.name;
      select.appendChild(selectOption);
    });

    select.addEventListener("change", (event: Event) => {
      this.selectedZoneId = (event.target as HTMLSelectElement).value;
    });

    selectContainer.appendChild(select);

    return selectContainer;
  }

  getZoneRiskData() {
    if (this.dustLayer.length > 0) {
      return;
    }
    const payload = new MappingHistoryPayload();
    payload.orgLevelId = this.selectedOrg?.id;
    payload.analyteId = this.selectedParameter?.id;
    payload.startDate = this.selectedDateRange?.start || 0;
    payload.endDate = this.selectedDateRange?.end || 0;
    payload.dateFilterType =
      this.selectedDateRange?.id || this.selectedDateRange?.id === 0
        ? this.selectedDateRange?.id
        : DateFilterTypeEnum.AllDates;
    payload.previousEndDate = this.selectedDateRange?.prevEnd
      ? this.selectedDateRange?.prevEnd
      : 0;
    payload.previousStartDate = this.selectedDateRange?.prevStart
      ? this.selectedDateRange?.prevStart
      : 0;
    payload.mappingGroupId = this.selectedZoneId;
    this.mappingHistoryService
      .getMappingHistory(payload)
      .pipe(takeUntil(this.baseDestroy$))
      .subscribe((res) => {
        if (res.length > 0) {
          this.dustLayer = res.map((item) => {
            item.currTime = this.dateFormatService.formatTimestampToString(
              item.timestamp,
              this.dateFormatService.userTimeFormat
            );
            item.currDate = this.dateFormatService.formatTimestampToString(
              item.timestamp,
              this.dateFormatService.userDateFormat
            );
            return item;
          });
          this.showPlayOption = true;
          this.showKmlFile(this.dustLayer.length - 1);
        }
      });
  }

  showKmlFile(index: number) {
    this.currentKmlIndex = index;
    this.markerEvent.next(false);
    this.pausedIndex = null;
    this.isPlaying = false;
    this.pausePlay();
  }

  startPlay() {
    this.isPlaying = !this.isPlaying;
    const resumeIndex = this.pausedIndex || 0;
    this.pausedIndex = !this.isPlaying ? this.currentKmlIndex : null;
    this.dustLayer.forEach((res, i) => {
      if (this.pausedIndex == null && this.isPlaying && i >= resumeIndex) {
        this.markersTimeout.push(
          setTimeout(() => {
            if (this.pausedIndex == null) {
              this.currentKmlIndex = i;
              this.markerEvent.next(false);
            } else {
              this.pausePlay();
            }
          }, 2000 * (i - resumeIndex))
        );
        if (i + 1 === this.dustLayer.length) {
          this.markersTimeout.push(
            setTimeout(() => {
              if (this.pausedIndex == null) {
                this.currentKmlIndex = null;
                this.markerEvent.next(true);
              }
            }, 2000 * (i - resumeIndex) + 2000)
          );
        }
      }
    });
  }

  pausePlay() {
    this.markersTimeout.forEach((item) => {
      clearTimeout(item);
    });
    this.markersTimeout = [];
  }
}
